/**
 * DashSidebar.tsx
 *
 * Persistent left-rail navigation for the customer dashboard area. Lives
 * inside DashShell so every customer-area page gets it for free.
 *
 * Behavior:
 *   - Desktop (≥lg): persistent sidebar, 64 wide (or 16 collapsed)
 *   - Tablet (md/sm): hidden by default, opened via a button in DashShell
 *   - Active route highlighted using react-router's useLocation
 *   - Optional badge counts on items (e.g. open tickets) via the `badges` prop
 *   - Glass/light style consistent with the rest of the design language
 */
import React from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import logoFull from '../../assets/logo.png';

interface NavItem {
  key: string;
  label: string;
  path: string;
  Icon: React.FC<{ className?: string }>;
  badgeKey?: 'openTickets' | 'expiringDomains';
  group?: 'services' | 'account';
}

interface Props {
  badges?: Partial<Record<'openTickets' | 'expiringDomains', number>>;
  collapsed?: boolean;
  onClose?: () => void;     // mobile drawer close
  isMobileOpen?: boolean;
}

const NAV: NavItem[] = [
  { key: 'home',     label: 'Dashboard',  path: '/dashboard',          Icon: IconHome,    group: 'services' },
  { key: 'hosting',  label: 'Hosting',    path: '/customer/hosting',   Icon: IconHosting, group: 'services' },
  { key: 'vps',      label: 'VPS',        path: '/customer/vps',       Icon: IconVps,     group: 'services' },
  { key: 'mail',     label: 'Mail',       path: '/email-dashboard',    Icon: IconMail,    group: 'services' },
  { key: 'domains',  label: 'Domains',    path: '/dashboard/domains',  Icon: IconGlobe,   group: 'services', badgeKey: 'expiringDomains' },
  { key: 'billing',  label: 'Billing',    path: '/dashboard/billing',  Icon: IconBilling, group: 'account' },
  { key: 'orders',   label: 'Orders',     path: '/dashboard/orders',   Icon: IconOrders,  group: 'account' },
  { key: 'support',  label: 'Support',    path: '/dashboard/support',  Icon: IconSupport, group: 'account', badgeKey: 'openTickets' },
  { key: 'profile',  label: 'Profile',    path: '/dashboard/profile',  Icon: IconUser,    group: 'account' },
];

const DashSidebar: React.FC<Props> = ({ badges = {}, collapsed = false, isMobileOpen = false, onClose }) => {
  const location = useLocation();
  const services = NAV.filter(n => n.group === 'services');
  const account = NAV.filter(n => n.group === 'account');

  // Match by longest prefix so /customer/hosting/abc highlights "Hosting".
  const activeKey = (() => {
    const matches = NAV
      .filter(n => location.pathname === n.path || location.pathname.startsWith(n.path + '/'))
      .sort((a, b) => b.path.length - a.path.length);
    return matches[0]?.key;
  })();

  const Body = (
    <div className="flex flex-col h-full">
      {/* Brand — real logo lockup (matches marketing Header).
          - Collapsed rail: icon-only (favicon-style) so it fits the narrow column.
          - Expanded: the full PNG lockup, height-locked, links to /. */}
      <div className={`flex items-center ${collapsed ? 'justify-center' : 'px-5'} py-5 border-b border-slate-200/60`}>
        <Link to="/" aria-label="12and22 home" className="inline-flex items-center">
          {collapsed ? (
            <img src="/logo-icon.svg" alt="12and22" className="w-8 h-8" />
          ) : (
            <img src={logoFull} alt="12and22" className="h-8 w-auto" />
          )}
        </Link>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto py-4 space-y-6">
        <NavGroup title="Services" items={services} activeKey={activeKey} badges={badges} collapsed={collapsed} onClick={onClose} />
        <NavGroup title="Account"  items={account}  activeKey={activeKey} badges={badges} collapsed={collapsed} onClick={onClose} />
      </nav>

      {/* Footer / logout */}
      <div className={`px-3 py-3 border-t border-slate-200/60`}>
        <button
          onClick={() => { localStorage.removeItem('token'); window.location.href = '/'; }}
          className={`w-full flex items-center ${collapsed ? 'justify-center' : 'gap-3 px-3'} py-2 text-sm text-slate-500 hover:text-slate-900 hover:bg-slate-100/60 rounded-lg transition`}
        >
          <IconLogout className="w-4 h-4" />
          {!collapsed && <span>Sign out</span>}
        </button>
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop */}
      <aside
        className={`hidden lg:flex fixed left-0 top-0 bottom-0 z-30 ${
          collapsed ? 'w-16' : 'w-60'
        } bg-white/70 backdrop-blur-xl border-r border-slate-200/60 transition-all`}
      >
        {Body}
      </aside>

      {/* Mobile drawer */}
      {isMobileOpen && (
        <div className="lg:hidden fixed inset-0 z-40 flex">
          <button
            aria-label="Close menu"
            onClick={onClose}
            className="absolute inset-0 bg-slate-900/30 backdrop-blur-sm"
          />
          <aside className="relative w-64 bg-white shadow-2xl animate-[fadeUp_.3s_ease]">
            {Body}
          </aside>
        </div>
      )}
    </>
  );
};

// ============================================================================
// NavGroup
// ============================================================================

const NavGroup: React.FC<{
  title: string;
  items: NavItem[];
  activeKey?: string;
  badges: Partial<Record<'openTickets' | 'expiringDomains', number>>;
  collapsed: boolean;
  onClick?: () => void;
}> = ({ title, items, activeKey, badges, collapsed, onClick }) => (
  <div>
    {!collapsed && (
      <div className="px-5 mb-2 text-[10px] uppercase tracking-[0.2em] text-slate-400">{title}</div>
    )}
    <ul className="space-y-0.5 px-3">
      {items.map(item => {
        const active = activeKey === item.key;
        const badgeVal = item.badgeKey ? badges[item.badgeKey] : 0;
        return (
          <li key={item.key}>
            <NavLink
              to={item.path}
              onClick={onClick}
              className={`group flex items-center ${collapsed ? 'justify-center px-2' : 'gap-3 px-3'} py-2 rounded-lg text-sm font-medium transition relative ${
                active
                  ? 'bg-gradient-to-r from-orange-50 to-amber-50 text-orange-700 shadow-[inset_2px_0_0_0_rgba(243,156,18,0.9)]'
                  : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100/60'
              }`}
              title={collapsed ? item.label : undefined}
            >
              <item.Icon className={`w-4 h-4 flex-shrink-0 ${active ? 'text-orange-600' : 'text-slate-400 group-hover:text-slate-700'}`} />
              {!collapsed && (
                <>
                  <span className="flex-1">{item.label}</span>
                  {badgeVal && badgeVal > 0 ? (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-md bg-rose-100 text-rose-700 font-semibold tabular-nums">
                      {badgeVal}
                    </span>
                  ) : null}
                </>
              )}
              {collapsed && badgeVal && badgeVal > 0 ? (
                <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-rose-500 shadow-[0_0_6px_rgba(225,29,72,0.6)]" />
              ) : null}
            </NavLink>
          </li>
        );
      })}
    </ul>
  </div>
);

// ============================================================================
// Icons (inline SVG, no deps)
// ============================================================================
function IconHome({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="m3 12 9-9 9 9" /><path d="M5 10v10h14V10" />
    </svg>
  );
}
function IconHosting({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="6" rx="2" /><rect x="3" y="14" width="18" height="6" rx="2" />
      <circle cx="7" cy="7" r="0.8" fill="currentColor" /><circle cx="7" cy="17" r="0.8" fill="currentColor" />
    </svg>
  );
}
function IconVps({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" /><path d="M7 8h10M7 12h10M7 16h6" />
    </svg>
  );
}
function IconMail({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" />
    </svg>
  );
}
function IconGlobe({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" />
    </svg>
  );
}
function IconBilling({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="5" width="18" height="14" rx="2" /><path d="M3 10h18M7 15h4" />
    </svg>
  );
}
function IconOrders({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 6h18M5 6v14h14V6M9 10h6M9 14h6" />
    </svg>
  );
}
function IconSupport({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
      <path d="M7.5 7.5a6.5 6.5 0 0 1 9 0M17 17a6.5 6.5 0 0 1-9 0M7.5 17 4 20M16.5 7.5 20 4M16.5 17 20 20M7.5 7.5 4 4" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}
function IconUser({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="8" r="4" /><path d="M4 21a8 8 0 0 1 16 0" />
    </svg>
  );
}
function IconLogout({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 16l4-4-4-4M21 12H9" /><path d="M14 4H5a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h9" />
    </svg>
  );
}

export default DashSidebar;
